import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TextField,
  Switch,
  Button,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Tooltip,
  InputAdornment,
  Divider,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  Science as ScienceIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Percent as PercentIcon,
  AttachMoney as MoneyIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import api from '../../config/axiosConfig';

const CATEGORY_COLORS = {
  Inflamable:  { bg: '#fff3e0', color: '#e65100', border: '#ffb74d' },
  Corrosivo:   { bg: '#fce4ec', color: '#c62828', border: '#ef9a9a' },
  Tóxico:      { bg: '#f3e5f5', color: '#6a1b9a', border: '#ce93d8' },
  Oxidante:    { bg: '#e8f5e9', color: '#1b5e20', border: '#81c784' },
  Criogénico:  { bg: '#e3f2fd', color: '#0d47a1', border: '#90caf9' },
  Radiactivo:  { bg: '#fafafa', color: '#212121', border: '#bdbdbd' },
  Cancerígeno: { bg: '#efebe9', color: '#3e2723', border: '#a1887f' },
  'N/A':       { bg: '#f5f5f5', color: '#757575', border: '#e0e0e0' },
};

const getCategoryStyle = (category) =>
  CATEGORY_COLORS[category] || { bg: '#f5f5f5', color: '#424242', border: '#bdbdbd' };

const ConfiguracionSustanciasCosto = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/hazardous-substance-costs');
      setRows((res.data || []).map(r => ({ ...r, _edited: false })));
      setDirty(false);
    } catch (e) {
      showSnack('Error al cargar la configuración: ' + (e.response?.data?.message || e.message), 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const showSnack = (message, severity = 'success') =>
    setSnack({ open: true, message, severity });

  const handleChange = (idx, field, value) => {
    setRows(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value, _edited: true };
      return updated;
    });
    setDirty(true);
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      const payload = rows.map(r => ({
        substanceCatalogId: r.substanceCatalogId,
        percentage:  parseFloat(r.percentage)  || 0,
        fixedAmount: parseFloat(r.fixedAmount) || 0,
        isActive:    r.isActive,
        notes:       r.notes || null,
      }));
      await api.post('/hazardous-substance-costs/batch', payload);
      showSnack('Configuración guardada correctamente');
      setDirty(false);
      setRows(prev => prev.map(r => ({ ...r, _edited: false })));
    } catch (e) {
      showSnack('Error al guardar: ' + (e.response?.data?.error || e.message), 'error');
    } finally {
      setSaving(false);
    }
  };

  const editedCount  = rows.filter(r => r._edited).length;
  const activeCount  = rows.filter(r => r.isActive && (parseFloat(r.percentage) > 0 || parseFloat(r.fixedAmount) > 0)).length;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ScienceIcon sx={{ fontSize: 32, color: '#7b1fa2' }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#212121' }}>
              Costos por Sustancia Peligrosa
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Configure el recargo que aplica cada sustancia al total de la cotización
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={load}
            disabled={loading || saving}
          >
            Recargar
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={handleSaveAll}
            disabled={saving || loading || !dirty}
            sx={{ bgcolor: '#7b1fa2', '&:hover': { bgcolor: '#6a1b9a' } }}
          >
            Guardar Cambios{editedCount > 0 ? ` (${editedCount})` : ''}
          </Button>
        </Box>
      </Box>

      {/* Summary cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card variant="outlined">
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Total sustancias</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>{rows.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card variant="outlined">
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Con recargo activo</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#7b1fa2' }}>{activeCount}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card variant="outlined">
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Modificados sin guardar</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: dirty ? '#e65100' : 'text.primary' }}>{editedCount}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Explanation */}
      <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
        <Typography variant="body2">
          <strong>¿Cómo funciona?</strong> Al calcular una cotización que declare esta sustancia, se aplicará:
          <code style={{ margin: '0 4px' }}>Recargo = (Total base × % / 100) + Monto fijo</code>
          Puede usar solo uno de los campos o ambos. Un recargo de <strong>0% y $0</strong> no afecta la cotización.
        </Typography>
      </Alert>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 'calc(100vh - 420px)', overflow: 'auto' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow sx={{ '& th': { bgcolor: '#f3e5f5', fontWeight: 700, fontSize: '0.78rem' } }}>
                  <TableCell sx={{ minWidth: 200 }}>Sustancia</TableCell>
                  <TableCell sx={{ minWidth: 120 }}>Categoría</TableCell>
                  <TableCell sx={{ minWidth: 140 }} align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      <PercentIcon fontSize="small" /> Porcentaje
                    </Box>
                  </TableCell>
                  <TableCell sx={{ minWidth: 160 }} align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      <MoneyIcon fontSize="small" /> Monto Fijo (MXN)
                    </Box>
                  </TableCell>
                  <TableCell sx={{ minWidth: 80 }} align="center">Activo</TableCell>
                  <TableCell sx={{ minWidth: 220 }}>Notas</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, idx) => {
                  const catStyle = getCategoryStyle(row.category);
                  const hasRecargo = parseFloat(row.percentage) > 0 || parseFloat(row.fixedAmount) > 0;
                  return (
                    <TableRow
                      key={row.substanceCatalogId}
                      sx={{
                        bgcolor: row._edited ? '#fdf6ff' : 'transparent',
                        '&:hover': { bgcolor: '#f9f0ff' },
                        opacity: row.isActive ? 1 : 0.55,
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: row._edited ? 600 : 400 }}>
                            {row.substanceName}
                          </Typography>
                          {hasRecargo && row.isActive && (
                            <Chip label="Con recargo" size="small"
                              sx={{ fontSize: '0.6rem', height: 18, bgcolor: '#e8eaf6', color: '#3949ab' }} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={row.category || '—'}
                          size="small"
                          sx={{
                            fontSize: '0.68rem',
                            bgcolor: catStyle.bg,
                            color: catStyle.color,
                            border: `1px solid ${catStyle.border}`,
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <TextField
                          size="small"
                          type="number"
                          value={row.percentage ?? 0}
                          onChange={e => handleChange(idx, 'percentage', e.target.value)}
                          inputProps={{ min: 0, max: 100, step: 0.5 }}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                          }}
                          sx={{ width: 110 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <TextField
                          size="small"
                          type="number"
                          value={row.fixedAmount ?? 0}
                          onChange={e => handleChange(idx, 'fixedAmount', e.target.value)}
                          inputProps={{ min: 0, step: 50 }}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                          }}
                          sx={{ width: 130 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={row.isActive ? 'Activo — clic para desactivar' : 'Inactivo — clic para activar'}>
                          <Switch
                            size="small"
                            checked={Boolean(row.isActive)}
                            onChange={e => handleChange(idx, 'isActive', e.target.checked)}
                            sx={{ '& .MuiSwitch-thumb': { bgcolor: row.isActive ? '#7b1fa2' : undefined } }}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          placeholder="Nota interna..."
                          value={row.notes || ''}
                          onChange={e => handleChange(idx, 'notes', e.target.value)}
                          fullWidth
                          inputProps={{ maxLength: 255 }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {dirty && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={handleSaveAll}
            disabled={saving}
            sx={{ bgcolor: '#7b1fa2', '&:hover': { bgcolor: '#6a1b9a' } }}
          >
            Guardar Cambios{editedCount > 0 ? ` (${editedCount})` : ''}
          </Button>
        </Box>
      )}

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ConfiguracionSustanciasCosto;
